import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, X, ArrowLeft, Save, Search, Upload, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../context/AuthContext';
import { useSubcategories, invalidateSubcategories } from '../hooks/useSubcategories';
import CategoryIcon, { CURATED_ICONS } from '../components/CategoryIcon';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (str) =>
  (str || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const emptyForm = () => ({
  id: null,
  parent: 'residencial',
  slug: '',
  name: '',
  icon_name: 'Sofa',
  icon_url: '',
  order: 0,
});

const SortableRow = ({ sub, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sub.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`category-card-${sub.slug}`}
      className="border border-border p-5 flex items-center gap-4 hover:border-primary transition-all bg-white"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${sub.slug}`}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary touch-none"
        aria-label="Arrastar para reordenar"
        title="Arrastar para reordenar"
      >
        <GripVertical className="w-5 h-5" strokeWidth={1.5} />
      </button>
      <div className="w-14 h-14 flex items-center justify-center bg-neutral-50 border border-border shrink-0">
        <CategoryIcon subcategory={sub} size={28} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-base truncate">{sub.name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {sub.slug} · ordem {sub.order}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(sub)}
          data-testid={`edit-category-${sub.slug}`}
          className="text-muted-foreground hover:text-primary transition-colors p-2"
          title="Editar"
        >
          <Edit className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <button
          onClick={() => onDelete(sub)}
          data-testid={`delete-category-${sub.slug}`}
          className="text-muted-foreground hover:text-destructive transition-colors p-2"
          title="Excluir"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};

const AdminCategories = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { grouped, loading, refresh } = useSubcategories();
  const [localGroups, setLocalGroups] = useState({ residencial: [], corporativo: [] });
  const [form, setForm] = useState(emptyForm());
  const [showModal, setShowModal] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    setLocalGroups({
      residencial: [...(grouped.residencial || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
      corporativo: [...(grouped.corporativo || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    });
  }, [grouped]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      toast.error('Acesso negado. Apenas administradores.');
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const filteredIcons = useMemo(() => {
    const q = iconSearch.trim().toLowerCase();
    if (!q) return CURATED_ICONS;
    return CURATED_ICONS.filter((n) => n.toLowerCase().includes(q));
  }, [iconSearch]);

  const openCreate = (parent = 'residencial') => {
    setForm({ ...emptyForm(), parent });
    setSlugTouched(false);
    setIconSearch('');
    setShowModal(true);
  };

  const openEdit = (sub) => {
    setForm({
      id: sub.id,
      parent: sub.parent,
      slug: sub.slug,
      name: sub.name,
      icon_name: sub.icon_name || '',
      icon_url: sub.icon_url || '',
      order: sub.order ?? 0,
    });
    setSlugTouched(true);
    setIconSearch('');
    setShowModal(true);
  };

  const handleNameChange = (v) => {
    setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm((f) => ({ ...f, icon_url: url }));
      toast.success('Ícone enviado');
    } catch (err) {
      toast.error('Falha ao enviar ícone');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Informe o nome da categoria');
    if (!form.slug.trim()) return toast.error('Informe o slug');
    if (!form.icon_url && !form.icon_name) return toast.error('Escolha um ícone ou envie uma imagem');
    setSaving(true);
    try {
      const payload = {
        parent: form.parent,
        slug: form.slug.trim(),
        name: form.name.trim(),
        icon_name: form.icon_url ? null : form.icon_name || null,
        icon_url: form.icon_url || null,
        order: Number(form.order) || 0,
      };
      if (form.id) {
        await axios.put(`${API_URL}/subcategories/${form.id}`, payload);
        toast.success('Categoria atualizada');
      } else {
        await axios.post(`${API_URL}/subcategories`, payload);
        toast.success('Categoria criada');
      }
      await invalidateSubcategories();
      await refresh();
      setShowModal(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erro ao salvar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Excluir "${sub.name}"? Produtos existentes com esta subcategoria continuarão no banco.`)) return;
    try {
      await axios.delete(`${API_URL}/subcategories/${sub.id}`);
      toast.success('Categoria removida');
      await invalidateSubcategories();
      await refresh();
    } catch (err) {
      toast.error('Erro ao remover');
    }
  };

  const handleDragEnd = async (parent, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = localGroups[parent] || [];
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    setLocalGroups((g) => ({ ...g, [parent]: reordered }));
    try {
      await axios.post(`${API_URL}/subcategories/reorder`, { ids: reordered.map((i) => i.id) });
      await invalidateSubcategories();
      await refresh();
      toast.success('Ordem atualizada');
    } catch (err) {
      toast.error('Falha ao salvar nova ordem');
      await refresh();
    }
  };

  if (authLoading || !user?.is_admin) return null;

  const renderGroup = (parent, title) => {
    const items = localGroups[parent] || [];
    return (
      <section data-testid={`categories-group-${parent}`} className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-light tracking-tight">{title}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Arraste pelo ícone <span className="inline-flex align-middle mx-1"><GripVertical className="w-3 h-3 inline" /></span>
              à esquerda para reordenar.
            </p>
          </div>
          <button
            onClick={() => openCreate(parent)}
            data-testid={`add-category-${parent}`}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2 uppercase tracking-widest text-xs hover:bg-black transition-all"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            Nova em {title}
          </button>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 border border-dashed border-border text-center">
            Nenhuma subcategoria em {title}.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => handleDragEnd(parent, e)}
          >
            <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((sub) => (
                  <SortableRow key={sub.id} sub={sub} onEdit={openEdit} onDelete={handleDelete} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>
    );
  };

  return (
    <div data-testid="admin-categories-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between mb-12">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-3"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Voltar ao painel
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight">Categorias & Ícones</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Gerencie as subcategorias exibidas no catálogo. Escolha um ícone da biblioteca ou envie uma imagem PNG/SVG personalizada. Arraste para reordenar.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground py-16 text-center">Carregando categorias...</p>
        ) : (
          <>
            {renderGroup('residencial', 'Residencial')}
            {renderGroup('corporativo', 'Corporativo')}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-3xl font-light tracking-tight">
                {form.id ? 'Editar categoria' : 'Nova categoria'}
              </h2>
              <button data-testid="close-category-modal" onClick={() => setShowModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Categoria pai
                  </label>
                  <select
                    data-testid="category-parent-input"
                    value={form.parent}
                    onChange={(e) => setForm({ ...form, parent: e.target.value })}
                    className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 outline-none"
                  >
                    <option value="residencial">Residencial</option>
                    <option value="corporativo">Corporativo</option>
                  </select>
                </div>
                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Ordem
                  </label>
                  <input
                    type="number"
                    data-testid="category-order-input"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: e.target.value })}
                    className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Nome
                </label>
                <input
                  type="text"
                  data-testid="category-name-input"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 outline-none"
                  placeholder="Ex: Mesas de Jantar"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Slug (identificador)
                </label>
                <input
                  type="text"
                  data-testid="category-slug-input"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm({ ...form, slug: slugify(e.target.value) });
                  }}
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 outline-none"
                  placeholder="mesa-jantar"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Usado internamente. Evite trocar após criar produtos com este identificador.
                </p>
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                  Ícone
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border border-border p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                      Imagem personalizada
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 flex items-center justify-center bg-neutral-50 border border-border">
                        {form.icon_url ? (
                          <img src={form.icon_url} alt="ícone" className="w-full h-full object-contain" />
                        ) : (
                          <span className="text-xs text-muted-foreground">Nenhum</span>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col gap-2">
                        <label className="flex items-center justify-center gap-2 border border-dashed border-border hover:border-primary p-3 cursor-pointer text-xs uppercase tracking-widest">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleIconUpload}
                            disabled={uploading}
                            data-testid="category-icon-upload"
                          />
                          <Upload className="w-4 h-4" strokeWidth={1.5} />
                          {uploading ? 'Enviando...' : 'Enviar imagem'}
                        </label>
                        {form.icon_url && (
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, icon_url: '' })}
                            className="text-xs text-muted-foreground hover:text-destructive"
                          >
                            Remover imagem
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Se enviada, tem prioridade sobre o ícone abaixo. PNG/SVG recomendado.
                    </p>
                  </div>

                  <div className={`border border-border p-4 ${form.icon_url ? 'opacity-50 pointer-events-none' : ''}`}>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                      Ou escolha um ícone
                    </p>
                    <div className="flex items-center gap-2 mb-3 border-b border-border pb-2">
                      <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <input
                        type="text"
                        placeholder="Buscar ícone..."
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        className="w-full bg-transparent outline-none text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                      {filteredIcons.map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setForm({ ...form, icon_name: name })}
                          title={name}
                          data-testid={`icon-option-${name}`}
                          className={`aspect-square flex items-center justify-center border transition-all ${
                            form.icon_name === name
                              ? 'border-primary bg-primary text-white'
                              : 'border-border hover:border-primary'
                          }`}
                        >
                          <CategoryIcon
                            subcategory={{ icon_name: name }}
                            size={20}
                            className={form.icon_name === name ? 'text-white' : ''}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-border p-4 flex items-center gap-4 bg-neutral-50">
                <div className="w-14 h-14 flex items-center justify-center bg-white border border-border">
                  <CategoryIcon subcategory={form} size={28} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Pré-visualização</p>
                  <p className="font-body text-base">{form.name || 'Sem nome'}</p>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-category-button"
                  className="flex items-center gap-2 flex-1 bg-primary text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black transition-all disabled:opacity-50"
                >
                  <Save className="w-4 h-4" strokeWidth={1.5} />
                  {saving ? 'Salvando...' : form.id ? 'Atualizar' : 'Criar categoria'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-transparent border border-primary text-primary px-8 py-4 uppercase tracking-widest text-xs hover:bg-primary hover:text-white transition-all"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
